import {
  faCircleArrowDown,
  faCircleArrowUp,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Area } from "~/components/charts/area";
import { BlockchainIcon } from "~/components/logos/blockchain";
import { Error } from "~/components/site/error";
import { item } from "~/components/site/layout";
import { AnalyticsNav, Page } from "~/components/stablecoins/analytics-nav";
import { Layout } from "~/components/stablecoins/layout";
import { Percent } from "~/components/stablecoins/percent";
import { SummaryCard } from "~/components/stablecoins/summary-card";
import { abbreviatedNum } from "~/lib/abbreviated-num";
import { toDateString } from "~/lib/charts";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";
import type { BlockchainId, ChainRollupData } from "~/lib/stablecoins.server";

const { desc, path, title } = ROUTE_DATA.chains;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export default function Chains(): JSX.Element {
  const [data, setData] = useState<ChainRollupData>();

  useEffect(() => {
    async function getData() {
      setData(await (await fetch("https://app.brale.xyz/api/chains")).json());
    }

    if (data == null) getData();
  }, [data]);

  return (
    <Layout>
      <motion.div
        variants={item}
        className="mb-16 flex flex-col gap-y-1 prose dark:prose-invert"
      >
        <h1 className="h2 mb-6">Top Chains by Market Cap</h1>
        <p className="subheading m-0">
          Stablecoin analytics for leading blockchains. Only native supply is
          considered in market data.
        </p>
      </motion.div>

      <motion.div variants={item}>
        <AnalyticsNav page={Page.CHAINS} />
      </motion.div>
      <motion.div
        variants={item}
        className="grid grid-rows-1 sm:grid-cols-3 mt-8 gap-x-6 gap-y-4"
      >
        <SummaryCard description={<p>Total Market Cap</p>}>
          {data?.total_mcap && (
            <div className="flex gap-x-2">
              <span>{`$${abbreviatedNum(data.total_mcap)}`}</span>
              <span className="text-secondary">USD</span>
            </div>
          )}
        </SummaryCard>
        <SummaryCard description="Change (1m)">
          <div className="flex items-center gap-x-2">
            {data?.mcap_30d_change && data.mcap_30d_change > 0 && (
              <FontAwesomeIcon
                icon={faCircleArrowUp}
                className="text-green-500"
              />
            )}
            {data?.mcap_30d_change && data.mcap_30d_change < 0 && (
              <FontAwesomeIcon
                icon={faCircleArrowDown}
                className="text-red-500"
              />
            )}

            {data?.mcap_30d_change && <Percent value={data?.mcap_30d_change} />}
          </div>
        </SummaryCard>
        <SummaryCard description="Dominance">
          <div className="flex items-center gap-x-2">
            {data?.dominant_chain && (
              <BlockchainIcon
                chainId={
                  data?.dominant_chain
                    .replace(" ", "-")
                    .toLowerCase() as BlockchainId
                }
              />
            )}
            {data?.dominant_chain && (
              <span className="sm:hidden md:inline-block">
                {data.dominant_chain}:
              </span>
            )}
            {data?.dominant_percentage && (
              <Percent colored={false} value={data?.dominant_percentage} />
            )}
          </div>
        </SummaryCard>
      </motion.div>
      <motion.div variants={item} className="relative mt-6 h-[30vh] w-full">
        {data && (
          <Area
            labels={data.chart.dates.map(toDateString)}
            options={{ maintainAspectRatio: false }}
            values={data.chart.values}
            reserveCurrency="USD"
          />
        )}
      </motion.div>
      <motion.div variants={item} className="mt-6 sm:mt-12">
        <div className="border border-primary h-min round col-span-2 overflow-hidden">
          <table className="min-w-full divide-y divide-primary text-black dark:text-white">
            <thead className="bg-gray-100 dark:bg-gray-900">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm sm:pl-6 font-normal"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm sm:table-cell font-normal"
                >
                  7d Change
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm sm:table-cell font-normal"
                >
                  1m Change
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-right text-sm font-normal whitespace-nowrap"
                >
                  Market Cap
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary dark:divide-gray-700 bg-white dark:bg-black">
              {data?.chains.map((t) => (
                <tr key={t.chain}>
                  <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm sm:w-auto sm:max-w-none sm:pl-6">
                    <div className="flex items-center gap-x-2">
                      <BlockchainIcon
                        chainId={
                          t.chain
                            .replace(" ", "-")
                            .toLocaleLowerCase() as BlockchainId
                        }
                      />

                      <span>{t.chain}</span>
                    </div>
                  </td>
                  <td className="hidden px-3 py-4 text-sm sm:table-cell text-right">
                    <Percent value={t.change_7d} />
                  </td>
                  <td className="hidden px-3 py-4 text-sm sm:table-cell text-right">
                    <Percent value={t.change_1m} />
                  </td>
                  <td className="flex gap-x-2 px-3 py-4 text-sm justify-end">
                    <span className="text-right">
                      ${abbreviatedNum(t.mcap)}
                    </span>{" "}
                    <span className="text-secondary text-right">USD</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </Layout>
  );
}

export function CatchBoundary() {
  return <Error statusCode={404} themed />;
}
